import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
// eslint-disable-next-line no-restricted-imports
import { dialogIdSelector } from '@components/Table/util/constants';
import { newColors } from '@components/Theme/colors';
import styled from '@emotion/styled';
import { FocusScope } from '@react-aria/focus';
import { DOMProps } from '@react-types/shared';
import { STATUS_DIALOG } from '@utils/zIndex';
import { cloneElement, FC, Fragment, ReactNode, useRef } from 'react';
import {
  AriaButtonProps,
  AriaModalOverlayProps,
  useModalOverlay,
  useOverlayTrigger,
} from 'react-aria';
import {
  OverlayTriggerProps,
  OverlayTriggerState,
  useOverlayTriggerState,
} from 'react-stately';
import {
  AlertActionButton,
  AlertDismissButton,
  SystemAlertHeader,
} from '../../shared/components';
import { SystemAlertType } from '../../shared/constants';

export interface ActionProps {
  label: string;
  onClick: () => void;
}

export interface DismissProps {
  label?: string;
  onClick?: () => void;
}

const ModalUnderlay = styled.div({
  position: 'fixed',
  zIndex: STATUS_DIALOG,
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  background: newColors.system.modal.underlay,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

interface StyledDialogProps {
  type: SystemAlertType;
}

const StyledDialog = styled.div<StyledDialogProps>((props) => ({
  background: newColors.system[props.type].background,
  width: '428px',
  maxWidth: 'calc(100vw - 32px)',
  height: 'auto',
  border: `1px solid ${newColors.system[props.type].accent}`,
  borderRadius: '4px',
  borderWidth: '1px 1px 1px 8px',
  zIndex: 1,
  cursor: 'auto',
  padding: '12px 16px',
}));

const Content = styled.div({
  margin: '8px 0 16px 24px',
  color: newColors.text.black,
});

const ButtonGroup = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
});

// ts-unused-exports:disable-next-line
export interface StatusDialogContentProps {
  children: ReactNode;
  title?: string | undefined;
  type: SystemAlertType;
  action?: ActionProps;
  dismiss?: DismissProps;
  onDismiss?: () => void;
}

export const StatusDialogContent: FC<StatusDialogContentProps> = ({
  children,
  title,
  type,
  action,
  dismiss,
  onDismiss,
}: StatusDialogContentProps) => {
  const handleDismissClick = (): void => {
    if (dismiss?.onClick) {
      dismiss.onClick();
    }
    if (onDismiss) {
      onDismiss();
    }
  };

  const handleActionClick = async (): Promise<void> => {
    if (action?.onClick) {
      action.onClick();
    }
    if (onDismiss) {
      onDismiss();
    }
  };

  return (
    <StyledDialog type={type} role="dialog">
      <SystemAlertHeader type={type} title={title} />
      <Content>{children}</Content>
      <ButtonGroup>
        {action && (
          <AlertActionButton
            type={type}
            action={{
              label: action.label,
              onClick: handleActionClick,
            }}
          >
            <Trans>Action</Trans>
          </AlertActionButton>
        )}
        <AlertDismissButton
          type={type}
          onClick={handleDismissClick}
          dismiss={dismiss}
        >
          {dismiss?.label ? dismiss.label : t`Dismiss`}
        </AlertDismissButton>
      </ButtonGroup>
    </StyledDialog>
  );
};

interface ModalProps {
  state: OverlayTriggerState;
  children: ReactNode;
  title?: string | undefined;
  type: SystemAlertType;
  action?: ActionProps;
  dismiss?: DismissProps;
  props: AriaModalOverlayProps;
}

const Modal: FC<ModalProps> = ({
  state,
  children,
  title,
  type,
  action,
  dismiss,
  props,
}: ModalProps) => {
  const ref = useRef(null);
  const { modalProps, underlayProps } = useModalOverlay(props, state, ref);

  // This code exists because if we are currently in a table expand mode, we are inherently using Spectrum Dialog which has a focus lock on it. If we additionally try to use another dialog which has its own focus lock, they fight and can crash the browser. So we opt out of focus lock in this specific case.
  const tableIsInExpandModeRef = useRef(
    Boolean(document.querySelector(dialogIdSelector)),
  );
  const FocusScopeComp = tableIsInExpandModeRef?.current
    ? Fragment
    : FocusScope;

  return (
    <ModalUnderlay {...underlayProps}>
      <FocusScopeComp contain restoreFocus autoFocus>
        <div {...modalProps} ref={ref}>
          <StatusDialogContent
            type={type}
            title={title}
            action={action}
            dismiss={dismiss}
            onDismiss={state.close}
          >
            {children}
          </StatusDialogContent>
        </div>
      </FocusScopeComp>
    </ModalUnderlay>
  );
};

interface DialogProps {
  state: OverlayTriggerState;
  overlayProps: DOMProps;
  children: (close: () => void) => JSX.Element;
  title?: string | undefined;
  type: SystemAlertType;
  action?: ActionProps;
  dismiss?: DismissProps;
  props: anyOk;
}

const Dialog: FC<DialogProps> = ({
  state,
  overlayProps,
  children,
  title,
  type,
  action,
  dismiss,
  ...props
}: DialogProps) => {
  return (
    <>
      {state.isOpen && (
        <Modal
          state={state}
          type={type}
          action={action}
          dismiss={dismiss}
          title={title}
          {...props}
        >
          {cloneElement(children(state.close), overlayProps)}
        </Modal>
      )}
    </>
  );
};

interface StatusDialogProps {
  dialogProps: {
    overlayProps: DOMProps;
    state: OverlayTriggerState;
  };
  children: ReactNode;
  title?: string | undefined;
  action?: ActionProps;
  dismiss?: DismissProps;
  type: SystemAlertType;
}

export const StatusDialog: FC<StatusDialogProps> = ({
  dialogProps,
  children,
  title,
  action,
  dismiss,
  type,
  ...props
}: StatusDialogProps) => {
  return (
    <Dialog
      {...dialogProps}
      type={type}
      action={action}
      dismiss={dismiss}
      props={props}
      title={title}
    >
      {(): JSX.Element => <div>{children}</div>}
    </Dialog>
  );
};

interface StatusDialogHookProps {
  dialogProps: { overlayProps: DOMProps; state: OverlayTriggerState };
  triggerProps: AriaButtonProps<'button'> & { onClick: anyOk };
  overlayProps: DOMProps;
  state: OverlayTriggerState;
}

export const useStatusDialog = (
  opts: OverlayTriggerProps,
): StatusDialogHookProps => {
  const state = useOverlayTriggerState(opts);
  const { triggerProps, overlayProps } = useOverlayTrigger(
    { type: 'dialog' },
    state,
  );
  return {
    dialogProps: { overlayProps, state },
    triggerProps: { ...triggerProps, onClick: triggerProps.onPress },
    overlayProps,
    state,
  };
};
